/**
 * KillSwitchPanel (J6, Wave 3) — the one big red handle, always at the top of
 * the page under the header chips (user-confirmed placement), even while
 * disengaged.
 *
 * Disengaged: outlined ERROR button that opens the destructive confirm dialog
 * (SDialog: contained error primary INSIDE the dialog only, explicit Cancel,
 * NO header X — designer invariant, AC-004). Engaged: halted banner + the
 * "In progress while halted" list rendered from the GATES response
 * `rollbackLinks[]` (server-computed label/status/routingHashVerified) as
 * links via `rollbackHref` — NO auto-mass-rollback ever happens here, and
 * rollback links are never gated by the kill switch (backend rule).
 *
 * Composition per .design/autonomy-facade/mockups/autonomy-kill-switch.html.
 */

import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';
import { AUTONOMY_TEST_IDS } from '../testIds';
import type { AutonomyRollbackLink } from '../types/gates.types';

export interface KillSwitchPanelProps {
  /** Server-resolved kill-switch state (settings response). */
  engaged: boolean;
  /** True while ANY settings save is in flight (one action at a time). */
  busy: boolean;
  /** True while the kill-switch toggle itself is saving. */
  saving: boolean;
  /** Server-computed in-progress rollouts from the GATES response (AC-004). */
  rollbackLinks: AutonomyRollbackLink[];
  onEngage: () => void;
  onDisengage: () => void;
}

/** Dialog copy (from the confirmed mock — kept verbatim). */
const HALTS = [
  'Agent write tools (draft candidates, hypothesis drafts) — refused at the write gate',
  'Winner-rollout execution — blocked server-side, zero mutations',
  'Safe-rollout auto-escalation — monitoring keeps reading, stops acting',
];

function RollbackRow({ link }: { link: AutonomyRollbackLink }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2.5,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2">
          {link.label}
          <Chip
            label={link.kind}
            size="small"
            variant="outlined"
            sx={{ ml: 0.75, fontSize: '0.68rem', height: 18 }}
          />
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {link.status}
          {link.routingHashVerified && (
            <Box component="span" sx={{ color: 'success.main', whiteSpace: 'nowrap' }}>
              {' '}· routingHash verified ✓
            </Box>
          )}
        </Typography>
      </Box>
      <Button
        data-testid={AUTONOMY_TEST_IDS.rollbackLink(link.id)}
        variant="outlined"
        size="small"
        href={link.rollbackHref}
        sx={{ whiteSpace: 'nowrap', flex: 'none' }}
      >
        Roll back
      </Button>
    </Box>
  );
}

export const KillSwitchPanel: React.FC<KillSwitchPanelProps> = ({
  engaged,
  busy,
  saving,
  rollbackLinks,
  onEngage,
  onDisengage,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleConfirm = () => {
    setConfirmOpen(false);
    onEngage();
  };

  if (engaged) {
    return (
      <Box sx={{ mb: 2 }}>
        {/* Halted banner + the disengage affordance */}
        <Alert
          severity="error"
          data-testid={AUTONOMY_TEST_IDS.haltedBanner}
          action={(
            <Button
              data-testid={AUTONOMY_TEST_IDS.killSwitch}
              color="error"
              variant="outlined"
              size="small"
              disabled={busy}
              onClick={onDisengage}
            >
              Disengage
            </Button>
          )}
        >
          <AlertTitle>Kill switch engaged — automation halted</AlertTitle>
          Agent writes, winner-rollout execution, and safe-rollout auto-escalation are refused
          for this project. Monitoring reads continue. Nothing was rolled back automatically.
        </Alert>

        {/* One-click rollback links — server-computed, never auto-executed */}
        {rollbackLinks.length > 0 && (
          <Paper sx={{ mt: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
            >
              In progress while halted
            </Typography>
            {rollbackLinks.map((link) => (
              <RollbackRow key={link.id} link={link} />
            ))}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2.5, py: 1 }}>
              Rollback publishes the previous iteration and byte-verifies routing by hash — it is
              never blocked by the kill switch.
            </Typography>
          </Paper>
        )}
      </Box>
    );
  }

  return (
    <Paper sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Kill switch — disengaged ·{' '}
            <Box component="span" sx={{ color: 'success.main' }}>
              agent writes, rollout execution and auto-escalation active per lever tiers
            </Box>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            One switch halts agent writes, winner-rollout execution, and safe-rollout
            auto-escalation. It never rolls anything back automatically.
          </Typography>
        </Box>
        <Button
          data-testid={AUTONOMY_TEST_IDS.killSwitch}
          color="error"
          variant="outlined"
          disabled={busy}
          onClick={() => setConfirmOpen(true)}
          sx={{ whiteSpace: 'nowrap', flex: 'none' }}
        >
          Engage kill switch
        </Button>
      </Box>

      {/* Destructive confirm — the contained error primary lives ONLY here */}
      <SDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        showCloseButton={false}
        title="Engage kill switch?"
        data-testid={AUTONOMY_TEST_IDS.killSwitchDialog}
        secondaryAction={{
          label: 'Cancel',
          variant: 'text',
          onClick: () => setConfirmOpen(false),
          disabled: saving,
        }}
        primaryAction={{
          label: 'Engage kill switch',
          color: 'error',
          variant: 'contained',
          onClick: handleConfirm,
          loading: saving,
          'data-testid': AUTONOMY_TEST_IDS.killSwitchConfirm,
        }}
      >
        <Box sx={{ px: 3 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This halts every automated write on this project until you disengage it:
          </Typography>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1.5 }}>
            {HALTS.map((halt) => (
              <Box
                key={halt}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.5,
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <CloseIcon color="error" sx={{ fontSize: 16, flex: 'none' }} />
                <Typography variant="body2">{halt}</Typography>
              </Box>
            ))}
          </Box>
          <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            sx={{ borderLeft: '3px solid', borderColor: 'warning.main', pl: 1.25 }}
          >
            Nothing is rolled back automatically — in-progress rollouts stay live until you roll
            them back yourself. One-click rollback links stay available while halted.
          </Typography>
        </Box>
      </SDialog>
    </Paper>
  );
};

export default KillSwitchPanel;
